import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';
import { clsx } from 'clsx';
import { LegalLinks } from './LegalLinks';
import { useNavigation } from '../../hooks/useNavigation';
import { Globe, LogOut, Building2, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { signOut } from 'firebase/auth';
import { motion } from 'framer-motion';

interface DesktopSidebarProps {
  user: any;
  isLegalMenuOpen: boolean;
  setIsLegalMenuOpen: (open: boolean) => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  t: any;
}

export function DesktopSidebar({ user, isLegalMenuOpen, setIsLegalMenuOpen, isCollapsed, setIsCollapsed, t }: DesktopSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { navigationItems } = useNavigation(user);
  const { setUser } = useAuthStore();

  const handleLanguageChange = async (language: string) => {
    if (!user?.id) return;
    try {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        preferredLanguage: language,
        updatedAt: new Date()
      });
      setUser({
        ...user,
        preferredLanguage: language as 'FR' | 'EN' | 'IT'
      });
    } catch (error) {
      console.error('Error updating language:', error);
    }
  };

  const handleLogout = async () => {
    try {
      if (user?.id) {
        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, {
          isOnline: false,
          lastSeen: new Date()
        });
      }
      await signOut(auth);
      setUser(null);
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <aside
      className={clsx(
        'hidden md:flex md:flex-col md:fixed md:inset-y-0 transition-all duration-300 ease-in-out z-30',
        isCollapsed ? 'md:w-20' : 'md:w-72'
      )}
    >
      <div className="flex flex-col h-full bg-white/80 backdrop-blur-xl border-r border-slate-200/50">
        <div className={clsx(
          'flex items-center h-20 border-b border-slate-100/80',
          isCollapsed ? 'justify-center px-2' : 'px-6'
        )}>
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-600/20 group-hover:shadow-primary-600/30 group-hover:scale-[1.02] transition-all duration-200">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex flex-col"
              >
                <span className="text-lg font-bold text-slate-900 leading-tight tracking-tight">Expo 1</span>
                <span className="text-[11px] text-slate-400 font-medium tracking-wide">Paris 15e</span>
              </motion.div>
            )}
          </Link>
        </div>

        {!isCollapsed && (
          <div className="px-4 mt-5">
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50/80 border border-slate-100/80 hover:border-slate-200/80 transition-colors">
              <Globe className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <select
                value={user?.preferredLanguage || 'FR'}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="flex-1 text-sm bg-transparent border-none focus:outline-none focus:ring-0 text-slate-600 font-medium cursor-pointer"
              >
                <option value="FR">Francais</option>
                <option value="EN">English</option>
                <option value="IT">Italiano</option>
              </select>
            </div>
          </div>
        )}

        <nav className={clsx(
          'flex-1 overflow-y-auto mt-4',
          isCollapsed ? 'px-2' : 'px-3'
        )}>
          <div className="space-y-0.5">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path === '/admin/discussion' && location.pathname.startsWith('/admin'));

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={isCollapsed ? item.name : undefined}
                  className={clsx(
                    'group flex items-center rounded-xl transition-all duration-200 relative',
                    isCollapsed ? 'justify-center p-3' : 'px-3 py-2.5 gap-3',
                    isActive
                      ? 'bg-primary-50/80 text-primary-700'
                      : 'text-slate-500 hover:bg-slate-50/80 hover:text-slate-800'
                  )}
                >
                  {isActive && (
                    <motion.div
                      layoutId="sidebar-indicator"
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-primary-600 rounded-r-full"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                  <item.icon
                    className={clsx(
                      'flex-shrink-0 h-[18px] w-[18px] transition-colors duration-200',
                      isActive ? 'text-primary-600' : 'text-slate-400 group-hover:text-slate-500'
                    )}
                  />
                  {!isCollapsed && (
                    <div className="flex-1 min-w-0">
                      <span className={clsx(
                        'text-[13px] font-medium block truncate',
                        isActive ? 'text-primary-700 font-semibold' : ''
                      )}>
                        {item.name}
                      </span>
                    </div>
                  )}
                  {!isCollapsed && item.badge && (
                    <span className="flex-shrink-0 inline-flex items-center justify-center h-5 min-w-5 px-1.5 text-[10px] font-bold rounded-full bg-red-500 text-white">
                      {item.badge}
                    </span>
                  )}
                  {isCollapsed && item.badge && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="flex-shrink-0 border-t border-slate-100/80">
          {!isCollapsed && (
            <LegalLinks
              isOpen={isLegalMenuOpen}
              setIsOpen={setIsLegalMenuOpen}
              t={t}
            />
          )}

          <div className={clsx(
            'border-t border-slate-100/80',
            isCollapsed ? 'px-2 py-3' : 'px-3 py-3'
          )}>
            <button
              onClick={handleLogout}
              title={isCollapsed ? t.logout : undefined}
              className={clsx(
                'flex items-center w-full rounded-xl text-sm font-medium text-slate-400 hover:text-red-500 hover:bg-red-50/80 transition-all duration-200',
                isCollapsed ? 'justify-center p-3' : 'px-3 py-2.5 gap-3'
              )}
            >
              <LogOut className="h-[18px] w-[18px] flex-shrink-0" />
              {!isCollapsed && <span className="text-[13px]">{t.logout}</span>}
            </button>
          </div>

          <div className={clsx(
            'border-t border-slate-100/80',
            isCollapsed ? 'px-2 py-2' : 'px-3 py-2'
          )}>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="flex items-center justify-center w-full p-2 rounded-xl text-slate-300 hover:text-slate-500 hover:bg-slate-50/80 transition-all"
            >
              {isCollapsed ? (
                <ChevronsRight className="h-4 w-4" />
              ) : (
                <div className="flex items-center gap-2 text-xs">
                  <ChevronsLeft className="h-4 w-4" />
                  <span>Reduire</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
